import { useState, useEffect, useRef } from 'react';
import type { Image } from '../editor/fields/media-props';
import type { MediaType } from '../editor/fields/media-type-config';
import { extractMediaDate } from '../editor/media/metadata-date';
import type { MediaMetadata } from '../editor/media/metadata-date';

interface MediaMetadataResponse extends MediaMetadata {
  file_size?: number | string | null;
  name?: string;
  [key: string]: unknown;
}

type DataSourceFetcher = (
  mediaID: string,
  callback: (err: Error | null, result: MediaMetadataResponse | null | undefined) => void,
) => void;

interface DataSourceLike {
  getPhoto?: DataSourceFetcher;
  getVideo?: DataSourceFetcher;
  getAudio?: DataSourceFetcher;
  getSketch?: DataSourceFetcher;
  getAttachment?: DataSourceFetcher;
}

/**
 * Returns the appropriate dataSource fetch method for a given media type.
 * Returns null for media types that don't have a per-item metadata API
 * (signature has no getSignature in fulcrum-core — size comes from upload-time ref only).
 */
function getMetadataFetcher(
  dataSource: DataSourceLike,
  mediaType: MediaType | undefined,
): DataSourceFetcher | null {
  switch (mediaType) {
    case 'photo':
      return typeof dataSource.getPhoto === 'function'
        ? dataSource.getPhoto.bind(dataSource)
        : null;
    case 'video':
      return typeof dataSource.getVideo === 'function'
        ? dataSource.getVideo.bind(dataSource)
        : null;
    case 'audio':
      return typeof dataSource.getAudio === 'function'
        ? dataSource.getAudio.bind(dataSource)
        : null;
    case 'sketch':
      return typeof dataSource.getSketch === 'function'
        ? dataSource.getSketch.bind(dataSource)
        : null;
    case 'attachment':
      return typeof dataSource.getAttachment === 'function'
        ? dataSource.getAttachment.bind(dataSource)
        : null;
    default:
      return null;
  }
}

interface MetadataMap {
  [mediaID: string]: { fileSize?: number; fileName?: string; createdAt?: string | null };
}

/**
 * Fetches metadata (file_size, name, created date) for persisted media items on mount.
 * Returns a map of mediaID → { fileSize, fileName, createdAt } which the caller
 * merges into the hydrated Image items.
 *
 * Fetches for media types with a dataSource method (photo, video, audio, sketch, attachment).
 * Signature has no getSignature in fulcrum-core — size comes from the upload-time ref only.
 * Gracefully degrades: null file_size, fetch errors, and unmount during
 * fetch are all handled without surfacing errors to the user.
 */
function useMediaItemMetadata(
  items: Image[],
  dataSource: DataSourceLike | undefined,
  mediaType: MediaType | undefined,
): MetadataMap {
  const [metadata, setMetadata] = useState<MetadataMap>({});
  const fetchedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    if (!dataSource || !mediaType) {
      return () => {};
    }

    const fetcher = getMetadataFetcher(dataSource, mediaType);

    if (!fetcher) {
      // No fetcher for this media type (e.g. signature has no getSignature).
      // Record empty entries so the caller knows fetching is not applicable.
      setMetadata((prev) => {
        let changed = false;
        const next = { ...prev };
        items.forEach((item) => {
          if (item.mediaID && !prev[item.mediaID]) {
            next[item.mediaID] = {};
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      return () => {};
    }

    // Use a ref to track in-flight IDs so metadata changes from resolved
    // callbacks don't re-trigger this effect (which would cancel siblings).
    // On cleanup (including React Strict Mode's double-mount), we remove
    // only IDs whose callbacks haven't resolved yet — successfully resolved
    // IDs stay in the ref to prevent duplicate fetches on rerender.
    const itemsToFetch = items.filter(
      (item) => item.mediaID && !fetchedIdsRef.current.has(item.mediaID),
    );

    if (itemsToFetch.length === 0) {
      return () => {};
    }

    const pendingIds = new Set(itemsToFetch.map((item) => item.mediaID));
    const currentFetchedIds = fetchedIdsRef.current;
    pendingIds.forEach((id) => currentFetchedIds.add(id));

    const handleFetchResult = (
      mediaID: string,
      err: Error | null,
      result: MediaMetadataResponse | null | undefined,
    ) => {
      if (cancelled) return;

      // Mark as resolved so cleanup doesn't remove it from the ref
      pendingIds.delete(mediaID);

      if (err || !result) {
        // Record an empty entry so callers know fetching completed (even on failure)
        // and can show the post-fetch fallback (e.g. "Date: Unavailable").
        setMetadata((prev) => ({
          ...prev,
          [mediaID]: prev[mediaID] ?? {},
        }));
        return;
      }

      const rawSize = result.file_size;
      const fileSize = rawSize == null ? undefined : Number(rawSize);
      const validFileSize = fileSize === undefined || Number.isNaN(fileSize) ? undefined : fileSize;
      const fileName = typeof result.name === 'string' ? result.name : undefined;

      let createdAt: string | null = null;

      try {
        createdAt = extractMediaDate(result, mediaType);
      } catch {
        // Gracefully handle unexpected metadata shapes
      }

      // Only update if we have meaningful data
      if (validFileSize === undefined && fileName === undefined && createdAt === null) {
        // Still record the ID so the caller knows fetching completed (no data available).
        setMetadata((prev) => ({
          ...prev,
          [mediaID]: prev[mediaID] ?? {},
        }));
        return;
      }

      setMetadata((prev) => ({
        ...prev,
        [mediaID]: {
          ...(validFileSize === undefined ? {} : { fileSize: validFileSize }),
          ...(fileName ? { fileName } : {}),
          ...(createdAt ? { createdAt } : {}),
        },
      }));
    };

    itemsToFetch.forEach((item) => {
      fetcher(item.mediaID, handleFetchResult.bind(null, item.mediaID));
    });

    return () => {
      cancelled = true;
      // Only remove IDs still pending (callback never resolved) so the
      // next mount (Strict Mode or items change) retries them.
      pendingIds.forEach((id) => currentFetchedIds.delete(id));
    };
  }, [items, dataSource, mediaType]);

  return metadata;
}

export { useMediaItemMetadata };
export type { MetadataMap, DataSourceLike };
